/**
 * YD Product Sequence 2 Section
 * 序列帧滚动动画 + 多标题淡入淡出 + 星球时间换算
 */
(function() {
  'use strict';

  /** 图片格式检测 - 支持 AVIF 检测与缓存 */
  const imageFormatDetector = {
    _cache: {},

    async isSupported(format) {
      if (this._cache[format] !== undefined) return this._cache[format];
      if (format === 'avif') {
        this._cache[format] = await this._checkAvifSupport();
        return this._cache[format];
      }
      return false;
    },

    _checkAvifSupport() {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width > 0 && img.height > 0);
        img.onerror = () => resolve(false);
        img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABogQEAwgMgwYAAAAKAAAAB4A';
        setTimeout(() => resolve(false), 1000);
      });
    }
  };

  /** 星球时间控制器 - 实时时间显示和星球时间换算 */
  class PlanetTimeController {
    constructor(config) {
      this.config = config;
      this.timeElements = {};
      this.planetConfigs = {};
      this.updateInterval = null;
      this.isRunning = false;
      this.activePlanet = null;
    }

    initialize() {
      for (const planet of this.config.planetDisplay?.planets || []) {
        const timeEl = document.querySelector(`.planet-time-value[data-planet="${planet.id}"]`);
        if (timeEl) {
          this.timeElements[planet.id] = timeEl;
          this.planetConfigs[planet.id] = { rotationPeriodHours: planet.rotationPeriodHours };
        }
      }
    }

    startTimeUpdate(planetId) {
      if (this.activePlanet === planetId && this.isRunning) return;
      this.activePlanet = planetId;
      this.stopTimeUpdate();
      this.updateTime();
      const interval = this.config.planetDisplay?.timeUpdateInterval || 1000;
      this.updateInterval = setInterval(() => this.updateTime(), interval);
      this.isRunning = true;
    }

    stopTimeUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
      this.isRunning = false;
    }

    updateTime() {
      if (!this.activePlanet) return;
      const timeEl = this.timeElements[this.activePlanet];
      const planetConfig = this.planetConfigs[this.activePlanet];
      if (!timeEl || !planetConfig) return;
      timeEl.textContent = this.convertToPlanetTime(new Date(), planetConfig.rotationPeriodHours);
    }

    convertToPlanetTime(earthTime, rotationPeriodHours) {
      const earthSeconds = earthTime.getHours() * 3600 + earthTime.getMinutes() * 60 + earthTime.getSeconds();
      const planetSeconds = earthSeconds * (24 * 3600) / (rotationPeriodHours * 3600);
      const hours = Math.floor(planetSeconds / 3600) % 24;
      const minutes = Math.floor((planetSeconds % 3600) / 60);
      const seconds = Math.floor(planetSeconds % 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    dispose() {
      this.stopTimeUpdate();
      this.timeElements = {};
      this.planetConfigs = {};
      this.activePlanet = null;
    }
  }

  /**
   * 序列帧加载器：实现见 assets/yd-sequence-loader.js（全站共用）
   */
  class SequenceLoader extends window.YDSequenceLoader {
    constructor(config) {
      super({
        totalFrames: config.totalFrames,
        basePath: config.framePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath,
        priorityBase: config.priorityBase || 30000,
        progressMode: 'total'
      });
    }
  }

  class CanvasRenderer {
    constructor(canvasId) {
      this.canvas = document.querySelector(canvasId);
      if (!this.canvas) throw new Error(`Canvas not found: ${canvasId}`);
      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();
      // 200ms debounce 避免移动端地址栏滚动狂触发 resize
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.resizeCanvas(), 200);
      });
    }

    resizeCanvas() {
      // dpr 上限 2（与其它序列帧 renderer 一致；此前未限，dpr 3 设备位图达 5760 宽），
      // 且不超过「源帧宽 / CSS 宽」：源帧 1920 宽画到更大的位图只放大填充成本，不增清晰度
      const rect = this.canvas.getBoundingClientRect();
      let dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (this.sourceWidth && rect.width > 0) {
        dpr = Math.min(dpr, Math.max(1, this.sourceWidth / rect.width));
      }
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;
    }

    renderFrame(img) {
      if (!img) return;
      const srcW = img.naturalWidth || img.width;
      if (srcW && srcW > (this.sourceWidth || 0)) {
        this.sourceWidth = srcW;
        this.resizeCanvas();
      }
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;
      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        drawHeight = this.canvasHeight;
        drawWidth = this.canvasHeight * imgAspect;
        drawX = (this.canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = this.canvasWidth;
        drawHeight = this.canvasWidth / imgAspect;
        drawX = 0;
        drawY = (this.canvasHeight - drawHeight) / 2;
      }
      this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }
  }

  /** 序列帧动画控制器 - 帧停顿 + 星球信息展示 */
  class SequenceAnimator {
    constructor(config, loader, renderer) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;
      this.totalFrames = config.totalFrames;
      this.totalScrollDistance = 0;
      this.titleElements = {};
      this.currentFrame = 1;
      this.lastRenderedFrame = -1;
      this.scrollTrigger = null;
      this.viewportEnterTrigger = null;
      this.viewportEnterProgress = 0;
      this.introElements = { sidesContainer: null, leftText: null, rightText: null };
      // intro 尺寸缓存：避免 scrub onUpdate 每帧读 offsetWidth/getComputedStyle 触发强制同步布局。
      // 仅在初始化与 refresh 时测量一次，onUpdate 只用缓存值插值。
      this.introMetrics = null;
      this.planetTimeController = null;
      this.planetElements = {};
      this.currentActivePlanet = null;
      this.splitTextInstances = {};
      this.revealedTitles = new Set();
    }

    parseScrollHeight(value) {
      // 优先用 locked-vh 锁定值，避开 iOS Safari 地址栏导致的 svh 漂移
      const lockedVH = window.YDLockedVH && window.YDLockedVH.value;
      const viewportHeight = lockedVH ? lockedVH * 100 : window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return parseFloat(value) / 100 * viewportHeight;
      }
      return viewportHeight;
    }

    initSegmentMapping() {
      const segments = this.config.segments;
      if (!segments || segments.length === 0) {
        this.useSegmentMapping = false;
        return;
      }

      this.useSegmentMapping = true;
      this.segmentOffsets = [];
      let accumulatedVh = 0;

      for (const seg of segments) {
        const frames = seg.endFrame - seg.startFrame + 1;
        this.segmentOffsets.push({
          startFrame: seg.startFrame,
          endFrame: seg.endFrame,
          vhPerFrame: seg.vhPerFrame,
          startVh: accumulatedVh,
          frames: frames
        });
        accumulatedVh += frames * seg.vhPerFrame;
      }
      this.totalScrollVh = accumulatedVh;
    }

    // 计算总滚动距离（统一走 parseScrollHeight 用 locked-vh，避免地址栏伸缩漂移）
    // 供 initialize 与 createScrollTrigger 的函数式 end 在 refresh 时复用重算
    computeTotalScrollDistance() {
      if (this.config.scrollHeight) {
        this.totalScrollDistance = this.parseScrollHeight(this.config.scrollHeight);
      } else if (this.useSegmentMapping && this.totalScrollVh) {
        this.totalScrollDistance = this.parseScrollHeight(this.totalScrollVh + 'vh');
      } else {
        this.totalScrollDistance = this.parseScrollHeight('300vh');
      }
      return this.totalScrollDistance;
    }

    initialize() {
      this.initSegmentMapping();

      // 计算总滚动距离
      this.computeTotalScrollDistance();

      this.cacheTitleElements();
      this.initIntroAnimation();
      this.setupScrollSpacer();
      this.renderFrame(0);
      this.updateTitles(0);
      this.initPlanetDisplay();
      this.createScrollTrigger();

      if (!this.config.introAnimation?.enabled) {
        this.createViewportEnterTrigger();
      }

      // 帧加载在 init() 中后台进行，这里不再调用
    }

    cacheTitleElements() {
      const titles = this.config.titles || [];
      const hasIntroAnimation = this.config.introAnimation?.enabled;

      for (const titleConfig of titles) {
        if (!titleConfig.id) continue;
        const el = document.querySelector(`#${titleConfig.id}`);
        if (el) {
          this.titleElements[titleConfig.id] = el;
          const initialOpacity = (titleConfig.type === 'viewport-enter' && hasIntroAnimation) ? 1 : 0;
          gsap.set(el, { opacity: initialOpacity });
        }
      }
      this.initSplitText();
    }

    initSplitText() {
      if (typeof SplitText === 'undefined') {
        return;
      }

      // 处理 titles 配置中的标题（跳过 viewport-enter 类型）
      for (const titleConfig of this.config.titles || []) {
        if (titleConfig.type !== 'viewport-enter') {
          this.initSplitTextForElement(titleConfig.id);
        }
      }

      // 处理 planetDisplay.intro 的标题
      const planetIntro = this.config.planetDisplay?.intro;
      if (planetIntro?.id) {
        const el = document.querySelector(`#${planetIntro.id}`);
        if (el) {
          this.titleElements[planetIntro.id] = el;
          this.initSplitTextForElement(planetIntro.id);
        }
      }
    }

    initSplitTextForElement(elementId) {
      const container = this.titleElements[elementId];
      if (!container) return;
      // 空标题（如未配置文案的 #title-s2-intro）拆行得到 0 行，再 gsap.set 空 NodeList 会刷
      // "GSAP target not found" 警告；无文本直接跳过
      if (!container.textContent.trim()) return;

      const textElements = container.querySelectorAll('h1, h2, p');
      const targets = textElements.length > 0 ? textElements : [container];
      const instances = [];

      targets.forEach(el => {
        const split = new SplitText(el, { type: 'lines', linesClass: 'yd-line' });
        split.lines.forEach(line => {
          const wrapper = document.createElement('div');
          wrapper.className = 'yd-line-inner';
          wrapper.innerHTML = line.innerHTML;
          line.innerHTML = '';
          line.appendChild(wrapper);
        });
        instances.push(split);
        gsap.set(el.querySelectorAll('.yd-line-inner'), { yPercent: 100 });
      });

      this.splitTextInstances[elementId] = instances;
    }

    playLineReveal(titleId) {
      if (this.revealedTitles.has(titleId)) return;
      const container = this.titleElements[titleId];
      if (!container) return;

      const inners = container.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.revealedTitles.add(titleId);
      gsap.fromTo(inners, { yPercent: 100 }, { yPercent: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out' });
    }

    resetLineReveal(titleId) {
      if (!this.revealedTitles.has(titleId)) return;
      const container = this.titleElements[titleId];
      if (!container) return;

      const inners = container.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.revealedTitles.delete(titleId);
      gsap.set(inners, { yPercent: 100 });
    }

    initIntroAnimation() {
      const introConfig = this.config.introAnimation;
      if (!introConfig?.enabled) return;

      this.introElements.sidesContainer = document.querySelector(`#${introConfig.sidesContainerId}`);
      this.introElements.leftText = document.querySelector(`#${introConfig.leftTextId}`);
      this.introElements.rightText = document.querySelector(`#${introConfig.rightTextId}`);

      if (this.introElements.sidesContainer) {
        gsap.set(this.introElements.sidesContainer, { opacity: 1 });
      }

      const canvas = document.querySelector('#product-sequence-canvas-2');
      if (canvas) {
        gsap.set(canvas, { opacity: 0 });
      }

      this.measureIntroMetrics();
    }

    /**
     * 测量 intro 文字移动距离（一次性读 DOM）。
     * 这些尺寸只在布局变化（resize/refresh）时才变，scrub 期间恒定，
     * 因此缓存下来供 updateIntroAnimation 每帧复用，消除 layout thrashing。
     */
    measureIntroMetrics() {
      const { sidesContainer, leftText, rightText } = this.introElements;
      if (!sidesContainer || !leftText || !rightText) {
        this.introMetrics = null;
        return;
      }
      const containerWidth = sidesContainer.offsetWidth;
      const leftWidth = leftText.offsetWidth;
      const rightWidth = rightText.offsetWidth;
      const paddingX = 102; // 左右各 51px padding
      const gap = parseFloat(getComputedStyle(sidesContainer).gap) || 0;
      const availableSpace = containerWidth - paddingX - leftWidth - rightWidth - gap;
      this.introMetrics = {
        moveDistance: availableSpace / 2,
        canvas: document.querySelector('#product-sequence-canvas-2')
      };
    }

    initPlanetDisplay() {
      const planetConfig = this.config.planetDisplay;
      if (!planetConfig?.enabled) return;

      this.planetTimeController = new PlanetTimeController(this.config);
      this.planetTimeController.initialize();

      if (planetConfig.intro?.id) {
        const introEl = document.querySelector(`#${planetConfig.intro.id}`);
        if (introEl) {
          this.planetElements['intro'] = { element: introEl, config: planetConfig.intro };
          gsap.set(introEl, { opacity: 0 });
        }
      }

      for (const planet of planetConfig.planets || []) {
        const el = document.querySelector(`#${planet.elementId}`);
        if (el) {
          this.planetElements[planet.id] = { element: el, config: planet };
          gsap.set(el, { opacity: 0 });
        }
      }
    }

    setupScrollSpacer() {
      // 不再手动设置高度，由 ScrollTrigger 的 pinSpacing 自动处理
    }

    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
        return;
      }

      // 按当前 vh 重算 pin 总距离（intro 段 + 序列段），供函数式 end 在 refresh 时调用，
      // 同步更新 introDistance / totalPinDistance 供 handleScrollUpdateWithIntro 用
      const computePinDistance = () => {
        const hasIntroAnimation = this.config.introAnimation?.enabled;
        const introDistance = hasIntroAnimation
          ? this.parseScrollHeight(this.config.introAnimation.scrollDistance || '50svh')
          : 0;
        this.introDistance = introDistance;
        this.totalPinDistance = introDistance + this.computeTotalScrollDistance();
        return this.totalPinDistance;
      };

      computePinDistance();

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      // end 函数式 + invalidateOnRefresh：refresh 时按新 vh 重算，避免地址栏/横竖屏导致 pin 偏移
      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#sequence2-canvas-wrapper',
        start: 'top top',
        end: () => '+=' + computePinDistance(),
        pin: '#sequence2-canvas-wrapper',
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 0,  // 最低优先级，在 product-sequence(10) 和 video-scrub(5) 之后刷新
        invalidateOnRefresh: true,
        onRefresh: () => this.measureIntroMetrics(),  // 布局变化后重测 intro 缓存尺寸
        onUpdate: (self) => this.handleScrollUpdateWithIntro(self.progress)
      });
    }

    createViewportEnterTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
        return;
      }

      this.viewportEnterTrigger = ScrollTrigger.create({
        trigger: '#product-sequence-section-2',
        start: 'top bottom',
        end: 'top top',
        scrub: 1,
        onUpdate: (self) => {
          this.viewportEnterProgress = self.progress;
          this.updateViewportEnterTitles();
        },
        onLeaveBack: () => {
          this.viewportEnterProgress = 0;
          this.updateViewportEnterTitles();
        }
      });
    }

    updateViewportEnterTitles() {
      for (const titleConfig of this.config.titles || []) {
        if (titleConfig.type !== 'viewport-enter') continue;

        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        if (this.viewportEnterProgress === 0 && this.revealedTitles.has(titleConfig.id)) {
          this.resetLineReveal(titleConfig.id);
        }
        gsap.set(el, { opacity: this.viewportEnterProgress });
      }
    }

    handleScrollUpdateWithIntro(totalProgress) {
      const hasIntroAnimation = this.config.introAnimation?.enabled;

      if (hasIntroAnimation && this.introDistance > 0) {
        const introRatio = this.introDistance / this.totalPinDistance;

        if (totalProgress <= introRatio) {
          this.updateIntroAnimation(totalProgress / introRatio);
          this.renderFrame(0);
          this.updateTitles(0);
          return;
        }
        this.updateIntroAnimation(1);
        this.handleScrollUpdate((totalProgress - introRatio) / (1 - introRatio));
      } else {
        this.handleScrollUpdate(totalProgress);
      }
    }

    updateIntroAnimation(progress) {
      const { leftText, rightText } = this.introElements;
      if (!leftText || !rightText) return;

      // 用缓存尺寸，避免每帧读 offsetWidth/getComputedStyle（强制同步布局）。
      // 缓存缺失（极早期或元素未就绪）时补测一次。
      if (!this.introMetrics) this.measureIntroMetrics();
      if (!this.introMetrics) return;

      const { moveDistance, canvas } = this.introMetrics;

      gsap.set(leftText, { x: -moveDistance * progress });
      gsap.set(rightText, { x: moveDistance * progress });

      if (canvas) {
        gsap.set(canvas, { opacity: progress });
      }

      this.viewportEnterProgress = progress >= 1 ? 1 : (progress === 0 ? 0 : this.viewportEnterProgress);
    }

    handleScrollUpdate(progress) {
      let currentFrame;

      if (this.useSegmentMapping) {
        currentFrame = this.mapVhToFrame(progress * this.totalScrollVh);
      } else {
        currentFrame = Math.floor(progress * (this.totalFrames - 1));
      }

      currentFrame = Math.max(0, Math.min(this.totalFrames - 1, currentFrame));
      this.renderFrame(currentFrame);
      this.updateTitles(currentFrame);
      this.updatePlanetDisplay(currentFrame);
    }

    mapVhToFrame(scrolledVh) {
      for (const seg of this.segmentOffsets) {
        const segEndVh = seg.startVh + seg.frames * seg.vhPerFrame;
        if (scrolledVh <= segEndVh) {
          const vhIntoSeg = scrolledVh - seg.startVh;
          return Math.floor(seg.startFrame + vhIntoSeg / seg.vhPerFrame);
        }
      }
      return this.totalFrames - 1;
    }

    renderFrame(frameNumber) {
      if (frameNumber === this.lastRenderedFrame) return;
      this.currentFrame = frameNumber;
      const img = this.loader.getFrameSafe(frameNumber);
      if (img) this.renderer.renderFrame(img);
      // 只有目标帧真正画上才记录帧号：画的是回退帧或什么都没画时，帧到位后对同帧号的重绘不能被短路
      this.lastRenderedFrame = this.loader.getFrame(frameNumber) ? frameNumber : -1;
    }

    updateTitles(currentFrame) {
      for (const titleConfig of this.config.titles || []) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        // viewport-enter 类型：入场动画控制显示，之后按 fadeOut 淡出
        if (titleConfig.type === 'viewport-enter') {
          if (this.viewportEnterProgress >= 1 && titleConfig.fadeOut) {
            const { startFrame, endFrame } = titleConfig.fadeOut;
            let opacity = 1;
            if (currentFrame >= endFrame) {
              opacity = 0;
            } else if (currentFrame > startFrame) {
              opacity = 1 - (currentFrame - startFrame) / (endFrame - startFrame);
            }
            gsap.set(el, { opacity: Math.max(0, Math.min(1, opacity)) });
          }
          continue;
        }

        let opacity = 1;
        let shouldReveal = false;

        // 淡入逻辑
        if (titleConfig.fadeIn) {
          const { startFrame, endFrame } = titleConfig.fadeIn;
          if (currentFrame < startFrame) {
            opacity = 0;
          } else if (currentFrame < endFrame) {
            opacity = (currentFrame - startFrame) / (endFrame - startFrame);
            if (opacity > 0 && !this.revealedTitles.has(titleConfig.id)) shouldReveal = true;
          } else if (!this.revealedTitles.has(titleConfig.id)) {
            shouldReveal = true;
          }
        }

        // 淡出逻辑
        if (titleConfig.fadeOut && opacity > 0) {
          const { startFrame, endFrame } = titleConfig.fadeOut;
          if (currentFrame >= endFrame) {
            opacity = 0;
          } else if (currentFrame > startFrame) {
            opacity = 1 - (currentFrame - startFrame) / (endFrame - startFrame);
          }
        }

        if (opacity === 0 && this.revealedTitles.has(titleConfig.id)) {
          this.resetLineReveal(titleConfig.id);
        }
        if (shouldReveal) this.playLineReveal(titleConfig.id);
        gsap.set(el, { opacity: Math.max(0, Math.min(1, opacity)) });
      }
    }

    updatePlanetDisplay(currentFrame) {
      const planetConfig = this.config.planetDisplay;
      if (!planetConfig?.enabled) return;

      // 处理引导文字
      if (this.planetElements['intro']) {
        const introData = this.planetElements['intro'];
        const introConfig = introData.config;
        const introId = this.config.planetDisplay?.intro?.id;
        let introOpacity = 0;

        if (introConfig.fadeIn && introConfig.fadeOut) {
          const { fadeIn, fadeOut } = introConfig;
          if (currentFrame >= fadeIn.startFrame && currentFrame < fadeIn.endFrame) {
            introOpacity = (currentFrame - fadeIn.startFrame) / (fadeIn.endFrame - fadeIn.startFrame);
            if (introOpacity > 0 && introId && !this.revealedTitles.has(introId)) {
              this.playLineReveal(introId);
            }
          } else if (currentFrame >= fadeIn.endFrame && currentFrame <= fadeOut.startFrame) {
            introOpacity = 1;
            if (introId && !this.revealedTitles.has(introId)) this.playLineReveal(introId);
          } else if (currentFrame > fadeOut.startFrame && currentFrame <= fadeOut.endFrame) {
            introOpacity = 1 - (currentFrame - fadeOut.startFrame) / (fadeOut.endFrame - fadeOut.startFrame);
          }
        }
        gsap.set(introData.element, { opacity: Math.max(0, Math.min(1, introOpacity)) });
      }

      // 处理各星球信息
      let newActivePlanet = null;

      for (const planet of planetConfig.planets || []) {
        const planetData = this.planetElements[planet.id];
        if (!planetData) continue;

        const isVisible = currentFrame >= planet.fadeIn.start && currentFrame <= planet.fadeOut.end;
        if (isVisible) newActivePlanet = planet.id;
        gsap.set(planetData.element, { opacity: isVisible ? 1 : 0 });

        // 更新分割线进度条
        const dividerEl = planetData.element.querySelector('.planet-name-divider-progress');
        if (dividerEl) {
          const progress = isVisible
            ? (currentFrame - planet.fadeIn.start) / (planet.fadeOut.end - planet.fadeIn.start)
            : 0;
          dividerEl.style.width = `${Math.max(0, Math.min(100, progress * 100))}%`;
        }
      }

      // 管理时间更新
      if (newActivePlanet !== this.currentActivePlanet) {
        if (newActivePlanet) {
          this.planetTimeController?.startTimeUpdate(newActivePlanet);
        } else {
          this.planetTimeController?.stopTimeUpdate();
        }
        this.currentActivePlanet = newActivePlanet;
      }
    }

    dispose() {
      this.scrollTrigger?.kill();
      this.scrollTrigger = null;
      this.viewportEnterTrigger?.kill();
      this.viewportEnterTrigger = null;
      this.planetTimeController?.dispose();
      this.planetTimeController = null;
      this.planetElements = {};
      this.currentActivePlanet = null;
      this.viewportEnterProgress = 0;
      this.introElements = { sidesContainer: null, leftText: null, rightText: null };
    }
  }

  function waitForDependencies() {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(check);
      }, 10000);
    });
  }

  /**
   * 等待 video-scrub 初始化完成
   * 确保上游 section 的 ScrollTrigger pin 已计算完成
   */
  function waitForVideoScrubReady() {
    return new Promise((resolve) => {
      // 检查是否已 ready
      if (window.ydVideoScrubAnimator) {
        resolve();
        return;
      }

      // 监听 ready 事件
      const handler = () => {
        window.removeEventListener('yd-video-scrub-ready', handler);
        resolve();
      };
      window.addEventListener('yd-video-scrub-ready', handler);

      // 超时保护 - 15秒后强制初始化
      setTimeout(() => {
        window.removeEventListener('yd-video-scrub-ready', handler);
        resolve();
      }, 15000);
    });
  }

  async function init() {
    // 防止 HotReload 导致的重复初始化
    if (window.ydProductSequence2Animator) {
      return;
    }

    const section = document.querySelector('#product-sequence-section-2');
    if (!section) return;

    const configData = section.dataset.config;
    if (!configData) {
            return;
    }

    const config = JSON.parse(configData);
    await waitForDependencies();

    // 等待 video-scrub 初始化完成
    // 确保上游 pin 位置已计算完成
    await waitForVideoScrubReady();

    try {
      const loader = new SequenceLoader({
        totalFrames: config.totalFrames,
        framePath: config.framePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath
      });

      const renderer = new CanvasRenderer('#product-sequence-canvas-2');
      const animator = new SequenceAnimator(config, loader, renderer);

      // 先创建 ScrollTrigger，不等待帧加载
      animator.initialize();
      window.ydProductSequence2Animator = animator;
      loader.getFocusFrame = () => animator.currentFrame;

      // 帧加载：接近视口才开始（951 帧约 58MB）。section 顶进入视口底下方 1 屏内时启动
      // （本页几何下约等于用户在 seq1 scrub 走到 2/3 处），停在首屏不滚动就不下载；
      // 剩余帧经全站队列按离当前帧的距离继续加载。
      let frameLoadingStarted = false;
      const startFrameLoading = () => {
        if (frameLoadingStarted) return;
        frameLoadingStarted = true;
        loader.preloadPriority(config.priorityFrames || 92).then(() => {
          // 帧加载完成后，渲染当前帧
          if (animator.currentFrame >= 0) {
            animator.renderFrame(animator.currentFrame);
          }
          // 继续加载剩余帧
          loader.continuePreload();
        }).catch(() => {});
      };
      if (typeof ScrollTrigger !== 'undefined') {
        const gate = ScrollTrigger.create({
          trigger: section,
          start: 'top 200%',
          end: 'bottom top',
          once: true,
          onEnter: startFrameLoading
        });
        // 创建时已在范围内（或已滚过）不会触发 onEnter，手动补一次
        if (gate.isActive || gate.progress > 0) startFrameLoading();
      } else {
        startFrameLoading();
      }

          } catch (error) {
          }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
